/**
 * "Yo del Futuro 2030" — Experiencia inmersiva personalizada
 * Ruta: /yo-del-futuro
 *
 * Flujo:
 *  0) Form inicial: nombre, edad, (opcional) sustancia y frecuencia
 *  1) DataReveal estilo Mr. Robot (terminal glitch)
 *  2) Ringtone: "mensaje entrante desde número desconocido"
 *  3) Chat con 4 mensajes personalizados según bracket de edad (14-19 / 20-34 / 35-60)
 *     - Mensaje 2: checkboxes interactivos de "mentiras que me digo" (ajustadas por bracket)
 *     - Mensaje 4: foto de la silla de ruedas
 *  4) Pantalla de impacto con nombre del usuario en pantalla
 *  5) Escena alternativa "¿Y si cambio hoy?" — Yo 2030 recuperado
 *  6) CTA dual: Cotizador + 1412
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share2, ArrowRight, Zap, AlertTriangle } from "lucide-react";
import { Header } from "../components/Layout";

const SITE = "https://sinadicciones.org";
const WHEELCHAIR_IMG = "/assets/yo-futuro/silla_de_ruedas.jpg";
const INTRO_PRE_VIDEO = "/assets/yo-futuro/intro_pre.mp4";  // pantalla previa al click
const INTRO_BG_VIDEO = "/assets/yo-futuro/intro_bg.mp4";    // durante la narración (satélite)

// ============================================================================
// SCRIPTS — guiones personalizados por bracket de edad
// ============================================================================

function ageBracket(age) {
  if (age >= 14 && age <= 19) return "teen";
  if (age >= 20 && age <= 34) return "adult_young";
  return "adult_mature";
}

// Mentiras interactivas (ajustadas por bracket)
const LIES = {
  teen: [
    "Nadie se da cuenta",
    "Soy el más controlado del grupo",
    "Mis papás no lo saben",
    "Es solo cuando salgo, en casa estoy bien",
    "Todavía soy joven, tengo tiempo",
    "Si quisiera, paro mañana",
  ],
  adult_young: [
    "Voy a parar la próxima semana",
    "El trabajo me deja funcionar",
    "Mi pareja no lo sabe y no pasa nada",
    "Entre semana controlo, solo el fin de semana",
    "Con buen sueldo, no puedo ser adicto",
    "Una más y lo dejo para siempre",
  ],
  adult_mature: [
    "Mis hijos son chicos, no se dan cuenta",
    "Soy un adulto responsable",
    "Llevo años así y estoy bien",
    "El doctor no ha dicho nada",
    "Solo me daño yo, nadie más",
    "Me lo merezco, estoy muy estresado",
  ],
};

// ============================================================================
// TEST DE DEPENDENCIA — 5 preguntas con tono emocional (basadas en DSM-5 / CAGE-AID)
// ============================================================================

const DEPENDENCE_QUESTIONS = [
  {
    id: "mas_dias",
    prompt: "¿Consumes más días de los que tú mismo quisieras?",
    options: [
      { label: "Casi nunca", weight: 0 },
      { label: "A veces sí", weight: 1 },
      { label: "Más seguido de lo que admito", weight: 2 },
    ],
  },
  {
    id: "intento_parar",
    prompt: "¿Has intentado parar… y no has podido?",
    options: [
      { label: "Nunca lo he intentado", weight: 0 },
      { label: "Sí, y lo logré un tiempo", weight: 1 },
      { label: "Lo intenté, y no pude", weight: 2 },
    ],
  },
  {
    id: "alguien_dijo",
    prompt: "¿Alguien que te quiere te ha dicho que estás mal?",
    options: [
      { label: "No, nadie", weight: 0 },
      { label: "Alguna vez alguien me lo dijo", weight: 1 },
      { label: "Muchas veces. Ya no saben cómo decírmelo", weight: 2 },
    ],
  },
  {
    id: "solo_escondido",
    prompt: "¿Consumes solo, a escondidas, sin que nadie te vea?",
    options: [
      { label: "Nunca, siempre es con gente", weight: 0 },
      { label: "A veces, sí", weight: 1 },
      { label: "Seguido. Prefiero estar solo cuando lo hago", weight: 2 },
    ],
  },
  {
    id: "faltaste",
    prompt: "¿Has fallado a tu familia, tu pareja o tu trabajo por el consumo?",
    options: [
      { label: "No, todavía cumplo", weight: 0 },
      { label: "Una vez o dos", weight: 1 },
      { label: "Varias veces. Ya me acostumbré al cargo de conciencia", weight: 2 },
    ],
  },
];

// Score total 0–10 → 4 niveles
function scoreToLevel(score) {
  if (score <= 2) return "evitable";   // todavía hay margen real
  if (score <= 5) return "grave";      // empezando a perder control
  if (score <= 8) return "critico";    // dependencia instalada
  return "terminal";                    // al borde del punto sin retorno
}

// ============================================================================
// RECOMENDACIÓN CLÍNICA DE TRATAMIENTO (mapping ASAM + MINSAL Chile)
// ============================================================================
const TREATMENT_RECOMMENDATIONS = {
  evitable: {
    levelLabel: "Prevención temprana",
    modality: "online",
    modalityLabel: "Tratamiento Online",
    modalitySubtitle: "1 a 2 sesiones por semana · desde tu casa",
    reason: "Todavía tienes control, pero este es el momento exacto para evitar que se pierda. Unas sesiones con un terapeuta online pueden prevenir 10 años de dolor.",
    yoFuturoLine: "A mí me bastó con hablarlo a tiempo. Una vez a la semana, con alguien que sabía.",
    urgency: "normal",
  },
  grave: {
    levelLabel: "Empezando a perder el control",
    modality: "ambulatorio",
    modalityLabel: "Tratamiento Ambulatorio Intensivo",
    modalitySubtitle: "3 a 4 sesiones por semana · sigues con tu vida",
    reason: "Ya no basta con fuerza de voluntad. Necesitas estructura terapéutica varias veces a la semana, pero puedes seguir trabajando y con tu familia.",
    yoFuturoLine: "Lo que me salvó fue ir varias veces a la semana. Estructura. Horarios. Gente esperándome.",
    urgency: "alta",
  },
  critico: {
    levelLabel: "Dependencia instalada",
    modality: "residencial",
    modalityLabel: "Tratamiento Residencial",
    modalitySubtitle: "Internación 24/7 · 30 a 90 días",
    reason: "Necesitas un lugar donde no puedas consumir aunque quieras. Eso ya no lo logras con fuerza de voluntad. Un centro residencial te da ese entorno.",
    yoFuturoLine: "Necesitaba un lugar donde dormir, comer, y no poder consumir. Por un tiempo. Al principio lo odié. A los seis meses les debía la vida.",
    urgency: "urgente",
  },
  terminal: {
    levelLabel: "Al borde del punto sin retorno",
    modality: "residencial",
    modalityLabel: "Residencial con Desintoxicación Médica",
    modalitySubtitle: "Evaluación 24–48h · supervisión médica",
    reason: "Esto ya no es solo mental. Tu cuerpo lo necesita y dejarlo solo puede ser peligroso. Necesitas un equipo médico al lado desde el primer día.",
    yoFuturoLine: "Yo no llegué a tiempo a esto. Por favor, tú sí.",
    urgency: "critica",
  },
};

function recommendationForLevel(level) {
  return TREATMENT_RECOMMENDATIONS[level] || TREATMENT_RECOMMENDATIONS.grave;
}

// Reacción del yo-futuro después del test (2-3 mensajes adaptativos)
function buildReactionMessages(level, nombre) {
  const N = nombre;
  if (level === "evitable") {
    return [
      { text: `Ah…` },
      { text: `Ya me acordé, ${N}.` },
      { text: `Todavía no estábamos tan mal.` },
      { text: `Todavía tienes tiempo.` },
      { text: `Pero déjame contarte qué pasó con ese poco que parecía nada.` },
    ];
  }
  if (level === "grave") {
    return [
      { text: `Ya me acuerdo.` },
      { text: `Estábamos empezando a perder el control.` },
      { text: `${N}, escúchame bien.` },
      { text: `Porque lo que viene después… pasa rápido.` },
    ];
  }
  if (level === "critico") {
    return [
      { text: `Ah, ${N}…` },
      { text: `Ya me acuerdo. Ya estábamos mal.` },
      { text: `Muy mal.` },
      { text: `Necesito que me escuches con toda tu atención.` },
      { text: `Lo que viene es rápido. Y es feo.` },
    ];
  }
  // terminal
  return [
    { text: `Dios, ${N}…` },
    { text: `Estábamos peor de lo que recordaba.` },
    { text: `Mucho peor.` },
    { text: `Tienes que parar ya. Hoy.` },
    { text: `Poco después de este punto… se volvió irreversible.` },
  ];
}

function buildScript(bracket, { nombre, edad, edadFuturo, sustancia }, level) {
  const N = nombre;
  const sustLine = sustancia
    ? `El ${sustancia} ya no era solo los fines de semana.`
    : `Lo que consumías ya no era solo los fines de semana.`;

  // ==== M1 — mismo opener para todos
  const m1 = [
    { text: `${N}… soy tú.` },
    { text: `Te escribo desde el año 2030.` },
    { text: `Tengo ${edadFuturo} años. Y estoy cansado.` },
    { text: `Sé que ahora crees que lo tienes controlado.` },
    { text: `Que solo consumes "de vez en cuando".` },
    { text: `Que mañana vas a parar.` },
    { text: `Yo me decía exactamente las mismas mentiras.` },
  ];

  // ==== M2 — antes del checkbox interactivo
  const m2Opener = [
    { text: `${N}, antes de seguir…` },
    { text: `Quiero que veas las mentiras que nos repetíamos.` },
    { text: `Cada una. Todas.` },
    { text: `¿Cuáles te suenan? Marca las que te has dicho.`, isCheckboxPrompt: true },
  ];

  // ==== M3 — narrativa personalizada por bracket
  let m3 = [];
  if (bracket === "teen") {
    m3 = [
      { text: `Seguiste con las mismas juntas.` },
      { text: `Los mismos lugares. Los mismos "solo esta vez".` },
      { text: sustLine },
      { text: `Era los martes. Los jueves. A solas en mi pieza.` },
      { text: `Nunca terminé el colegio como quería.` },
      { text: `Lo dejé. Empecé y dejé tres veces.` },
      { text: `Cuando mis amigos se titulaban, yo consumía.` },
      { text: `Mi mamá lloró tantas noches que ya no la reconozco.` },
      { text: `Me botaron de la casa a los 22.` },
      { text: `Volví a los 24, peor.` },
      { text: `Mi hermano menor ya no me habla.` },
      { text: `Todo lo que iba a ser, ${N}… se quedó atrás.` },
    ];
  } else if (bracket === "adult_young") {
    m3 = [
      { text: `Seguiste con las mismas juntas.` },
      { text: `Los mismos lugares. Los mismos "solo esta vez".` },
      { text: sustLine },
      { text: `Después empecé a consumir solo.` },
      { text: `Perdí trabajos por faltar, por llegar tarde, por excusas.` },
      { text: `Mi pareja me veía destruirme y lloraba.` },
      { text: `Yo gritaba que "estaba todo bien".` },
      { text: `Se fue. Destruida. Llorando.` },
      { text: `Los hijos que queríamos tener… los tuve.` },
      { text: `Y me miraron con miedo, no con amor.` },
      { text: `Mi familia ya no me miraba a los ojos.` },
      { text: `Me arriesgué robando. Mintiendo. Manipulando.` },
      { text: `Lo perdí todo, ${N}. Todo lo que teníamos a los ${edad}.` },
    ];
  } else {
    // adult_mature
    m3 = [
      { text: `Seguiste con las mismas juntas.` },
      { text: `Los mismos lugares. Los mismos "solo esta vez".` },
      { text: sustLine },
      { text: `Mis hijos aprendieron a vivir con el miedo que les di.` },
      { text: `Aprendieron a no pedirme nada. A no esperar nada.` },
      { text: `Mi pareja se enfermó cuidándome.` },
      { text: `Mi salud se fue: hígado, corazón, presión.` },
      { text: `Mis papás murieron decepcionados.` },
      { text: `No alcancé a despedirme conscientemente.` },
      { text: `A los 50 ya no pude trabajar más.` },
      { text: `${N}, lo perdí todo. Todo lo que habíamos construido.` },
    ];
  }

  // ==== Ajuste de M3 según level (intensidad de dependencia detectada)
  // evitable: solo primer tercio + cierre esperanzado; grave: 2/3; crítico: todo; terminal: todo + cierre extra
  const lvl = level || "critico";
  if (lvl === "evitable") {
    const cut = Math.max(3, Math.ceil(m3.length * 0.4));
    m3 = m3.slice(0, cut);
    m3.push({ text: `Ahí paré, ${N}. Pero el daño tardó años en sanar.` });
    m3.push({ text: `Perdí amigos, confianza, una pareja. Nunca volví a ser el mismo.` });
  } else if (lvl === "grave") {
    const cut = Math.max(4, Math.ceil(m3.length * 0.7));
    m3 = m3.slice(0, cut);
    m3.push({ text: `Y eso solo fue el principio, ${N}.` });
  } else if (lvl === "terminal") {
    m3.push({ text: `Y eso fue solo los primeros años.` });
    m3.push({ text: `Después vino algo peor, ${N}.` });
    m3.push({ text: `No alcanzo a contártelo acá. No hay tiempo.` });
  }

  // ==== M4 — advertencia final universal + foto
  const m4 = [
    { text: `Si sigues por este camino…` },
    { text: `esto se vuelve irreversible.` },
    { text: `Te lo repito, ${N}: va a ser irreversible.` },
    { text: "", image: WHEELCHAIR_IMG, imageCaption: "Mi 2030. Lo que pude haber evitado." },
    { text: `Para ahora.` },
    { text: `Salva nuestro futuro.` },
    { text: `Cambia tus hábitos aunque duela.` },
    { text: `Evita a esa gente que consume.` },
    { text: `Habla con nuestra familia.` },
    { text: `Pide ayuda de verdad.` },
    { text: `Tú todavía puedes.` },
    { text: `Yo ya no pude.` },
  ];

  // ==== Opener del test (yo-futuro pide recordar)
  const testOpener = [
    { text: `${N}, espera.` },
    { text: `Antes de seguir… necesito recordar bien.` },
    { text: `Mi cabeza del futuro está borrosa.` },
    { text: `Ayúdame. Respóndeme 5 cosas.` },
    { text: `Necesito saber en qué punto estamos tú y yo.`, isTestPrompt: true },
  ];

  return {
    scenes: [
      { id: "m1", label: "Mensaje 1", messages: m1 },
      { id: "test", label: "— recordando —", messages: testOpener, isTest: true },
      { id: "m2", label: "Mensaje 2", messages: m2Opener, lies: LIES[bracket] },
      { id: "m3", label: "Mensaje 3", messages: m3 },
      { id: "m4", label: "Mensaje 4", messages: m4 },
    ],
  };
}

function buildAlternateScript(bracket, { nombre }, level) {
  const N = nombre;
  const rec = recommendationForLevel(level || "critico");

  const opener = [
    { text: `${N}… soy yo también.` },
    { text: `Pero del 2030 bueno.` },
    { text: `El que sí paró.` },
    { text: `El que sí pidió ayuda a tiempo.` },
    { text: `Ya vi lo que hay al otro lado.`, anchor: true },
    { text: `Y ${N}…` },
    { text: `La vida es linda sin ser esclavo de la droga.`, anchor: true },
  ];

  // Bloque de "cómo lo logré" — mención orgánica del tratamiento según level
  const howIMadeIt = [
    { text: `¿Sabes qué me salvó?` },
    { text: rec.yoFuturoLine },
    { text: `Se llama ${rec.modalityLabel.toLowerCase()}.` },
  ];

  let specific = [];
  if (bracket === "teen") {
    specific = [
      { text: `Terminé mis estudios.` },
      { text: `Tengo amigos que no necesitan consumir para reírse.` },
      { text: `Mi mamá duerme tranquila otra vez.` },
      { text: `Mi hermano menor me admira.` },
    ];
  } else if (bracket === "adult_young") {
    specific = [
      { text: `Mi pareja se quedó conmigo.` },
      { text: `Mis hijos me abrazan sin miedo.` },
      { text: `Tengo trabajo. Duermo tranquilo en la noche.` },
    ];
  } else {
    specific = [
      { text: `Mis hijos me escriben los domingos.` },
      { text: `Alcancé a despedirme bien de mis papás.` },
      { text: `Mi salud volvió. Camino todos los días.` },
    ];
  }

  const anchors = [
    { text: `Tengo amigos sanos.` },
    { text: `Tengo metas.` },
    { text: `Tengo personas que me apoyan y me quieren de verdad.`, anchor: true },
  ];

  const close = [
    { text: `Por favor, ${N}…` },
    { text: `Para ahora que puedes.` },
    { text: `Pide ayuda.` },
    { text: `Quiero esta vida.`, anchor: true },
    { text: `Quiero que tú también la tengas.` },
  ];

  return [...opener, ...howIMadeIt, ...specific, ...anchors, ...close];
}

// ============================================================================
// Helpers
// ============================================================================
function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// ============================================================================
// GlitchText — efecto RGB-split sutil para títulos/contactos
// ============================================================================
function GlitchText({ children, className = "", intensity = 1 }) {
  return (
    <span className={`relative inline-block ${className}`} style={{ textShadow: `${intensity}px 0 #ff3b3b, -${intensity}px 0 #00e5ff` }}>
      {children}
    </span>
  );
}

// ============================================================================
// NoiseBg — capa fija de ruido estático animado (SVG + flicker)
// ============================================================================
function NoiseBg() {
  return (
    <>
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.08] mix-blend-overlay" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />
      {/* scanlines */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.06]" style={{
        backgroundImage: "repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0px, rgba(255,255,255,0.6) 1px, transparent 1px, transparent 3px)",
      }} />
    </>
  );
}

// ============================================================================
// INTRO CINEMÁTICA — voz ElevenLabs + efectos glitch CSS sincronizados
// ============================================================================
// Texto del intro (sin audio). Las líneas aparecen con timers relativos.
const INTRO_LINES = [
  { at: 0.3, text: "Hoy interceptamos un mensaje...", big: true },
  { at: 2.6, text: "del año 2030.", accent: true },
  { at: 4.4, text: "Nadie sabe cómo llegó." },
  { at: 6.7, text: "Solo sabemos que es tuyo." },
  { at: 8.9, text: "Tiene un mensaje vital para tu existencia.", final: true },
];

// Intenta aplicar filtros de "señal corrupta" via Web Audio API (low-pass + reverb + ligero bitcrush).
// Si no está disponible, cae a <audio> plano.

function IntroCinematic({ onContinue }) {
  const [started, setStarted] = useState(false);
  const [flash, setFlash] = useState(false);
  const [year, setYear] = useState(2026);
  const timersRef = useRef([]);
  const finishedRef = useRef(false);
  const preVideoRef = useRef(null);
  const bgVideoRef = useRef(null);

  // Forzar autoplay del video pre-start (muted)
  useEffect(() => {
    if (started) return;
    const v = preVideoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    tryPlay();
    const onVis = () => { if (document.visibilityState === "visible") tryPlay(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [started]);

  // Forzar autoplay del video de la secuencia (satélite)
  useEffect(() => {
    if (!started) return;
    const v = bgVideoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    tryPlay();
    const onVis = () => { if (document.visibilityState === "visible") tryPlay(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [started]);

  function clearTimers() {
    timersRef.current.forEach((t) => {
      if (typeof t === "number") clearTimeout(t);
      else if (t && typeof t.clear === "function") t.clear();
    });
    timersRef.current = [];
  }

  function startSequence() {
    if (started) return;
    setStarted(true);
    clearTimers();

    // Contador de año 2026→2030 acelerado (~1s total)
    let y = 2026;
    const yearInterval = setInterval(() => {
      y += 1;
      setYear(y);
      if (y >= 2030) clearInterval(yearInterval);
    }, 180);
    timersRef.current.push({ clear: () => clearInterval(yearInterval) });

    // Revelar TODAS las líneas a la vez (animación rápida con stagger sutil vía Motion)

    // Flash rojo asociado a la línea "accent" (del año 2030)
    const flashT = setTimeout(() => {
      setFlash(true);
      const t2 = setTimeout(() => setFlash(false), 280);
      timersRef.current.push(t2);
    }, 650);
    timersRef.current.push(flashT);

    // Avanzar al form después de un tiempo de lectura razonable
    const end = setTimeout(() => {
      finishedRef.current = true;
      onContinue();
    }, 5200);
    timersRef.current.push(end);
  }

  function skip() {
    clearTimers();
    onContinue();
  }

  useEffect(() => {
    return () => {
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      className="fixed inset-0 z-[60] bg-black flex items-center justify-center overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="futuro-intro-cinematic"
    >
      {/* Static noise + scanlines */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.09] mix-blend-overlay" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: "repeating-linear-gradient(0deg, rgba(255,255,255,0.7) 0px, rgba(255,255,255,0.7) 1px, transparent 1px, transparent 3px)",
      }} />

      {/* Red flash overlay */}
      <AnimatePresence>
        {flash && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.55, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="pointer-events-none absolute inset-0 bg-red-600 mix-blend-screen"
          />
        )}
      </AnimatePresence>

      {/* Pre-start: pantalla previa con el video original — un solo botón inicia la secuencia */}
      {!started && (
        <>
          {/* Video pre-start con letterbox: fondo blureado + foreground contain */}
          <video
            src={INTRO_PRE_VIDEO}
            className="absolute inset-0 w-full h-full object-cover z-[1] blur-2xl scale-110 opacity-40"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
          />
          <video
            ref={preVideoRef}
            src={INTRO_PRE_VIDEO}
            className="absolute inset-0 w-full h-full object-contain z-[1]"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            data-testid="futuro-intro-pre-video"
          />
          <div className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-b from-black/75 via-transparent to-black/85" />
          <div className="pointer-events-none absolute inset-0 z-[2]" style={{ background: "radial-gradient(ellipse at center, transparent 25%, rgba(0,0,0,0.55) 100%)" }} />

          <div className="relative z-10 flex flex-col items-center px-6 text-center">
            <motion.p
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-[10px] uppercase tracking-[0.4em] text-cyan-400 mb-6 font-mono"
            >
              &gt; SEÑAL TEMPORAL DETECTADA
            </motion.p>
            <motion.h2
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="text-3xl sm:text-5xl font-light text-white mb-10 leading-tight"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                textShadow: "2px 0 #ff3b3b, -2px 0 #00e5ff",
              }}
            >
              Un mensaje<br />desde 2030.
            </motion.h2>
            <motion.button
              onClick={startSequence}
              data-testid="futuro-intro-start"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
              className="group px-10 py-4 bg-red-500 hover:bg-red-400 text-black font-bold uppercase tracking-[0.2em] text-sm rounded-md transition-all hover:scale-105 shadow-[0_0_60px_rgba(239,68,68,0.5)] font-mono flex items-center gap-2"
            >
              Iniciar transmisión
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          </div>
        </>
      )}

      {/* Secuencia en ejecución */}
      {started && (
        <>
          {/* Video del satélite — fondo cinematográfico cubre TODA la pantalla */}
          <video
            ref={bgVideoRef}
            src={INTRO_BG_VIDEO}
            className="absolute inset-0 w-full h-full object-cover z-[1]"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            data-testid="futuro-intro-bg-video"
          />
          <div className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-b from-black/70 via-black/35 to-black/85" />
          <div className="pointer-events-none absolute inset-0 z-[2]" style={{ background: "radial-gradient(ellipse at center, transparent 20%, rgba(0,0,0,0.65) 100%)" }} />

          {/* Banner terminal "SEÑAL INTERCEPTADA" — fijo en la parte superior del viewport */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 0.95, y: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute top-6 sm:top-10 left-1/2 -translate-x-1/2 z-20 text-[10px] sm:text-xs font-mono tracking-widest text-center pointer-events-none whitespace-nowrap"
            style={{ textShadow: "1px 0 #ff3b3b, -1px 0 #00e5ff" }}
          >
            <motion.div
              animate={{ opacity: [1, 0.55, 1, 0.85, 1] }}
              transition={{ duration: 0.7, repeat: 2 }}
              className="text-red-400"
            >
              &gt; SEÑAL INTERCEPTADA
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="text-cyan-400 mt-1"
            >
              &gt; ORIGEN: <span className="text-white">{year}</span> · DESTINATARIO: <span className="text-white">TÚ</span>
            </motion.div>
          </motion.div>

          <div className="relative z-10 flex flex-col items-center justify-center w-full px-6">
            {/* Bloque de texto: aparece de golpe con stagger mínimo (sensación simultánea) */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
              }}
              className="flex flex-col items-center gap-4 sm:gap-5 max-w-xl text-center"
            >
              {INTRO_LINES.map((line, i) => {
                const cls = line.big
                  ? "text-4xl sm:text-6xl"
                  : line.final
                    ? "text-xl sm:text-2xl text-white"
                    : line.small
                      ? "text-base text-neutral-400 tracking-widest uppercase font-mono"
                      : "text-xl sm:text-2xl text-neutral-200";
                const style = line.big || line.final
                  ? { fontFamily: "'Playfair Display', Georgia, serif", textShadow: "1px 0 #ff3b3b, -1px 0 #00e5ff" }
                  : {};
                return (
                  <motion.p
                    key={i}
                    variants={{
                      hidden: { opacity: 0, y: 14, filter: "blur(8px)" },
                      visible: { opacity: 1, y: 0, filter: "blur(0px)" },
                    }}
                    transition={{ duration: 0.55, ease: "easeOut" }}
                    className={`font-light leading-tight ${cls} ${line.accent ? "text-red-400" : ""}`}
                    style={style}
                  >
                    {line.text}
                  </motion.p>
                );
              })}
            </motion.div>
          </div>

          {/* Controles */}
          <div className="absolute top-3 right-3 z-20 flex gap-2">
            <button
              onClick={skip}
              data-testid="futuro-intro-skip"
              aria-label="Saltar"
              className="px-3 h-9 rounded-full bg-neutral-900/70 backdrop-blur text-neutral-300 hover:text-white text-xs font-mono border border-neutral-800"
            >
              Saltar →
            </button>
          </div>
        </>
      )}
    </motion.div>
  );
}

// ============================================================================
// 1) FORM INICIAL
// ============================================================================
function IntakeForm({ onSubmit }) {
  const [nombre, setNombre] = useState("");
  const [edad, setEdad] = useState(24);
  const [sustancia, setSustancia] = useState("");
  const [frecuencia, setFrecuencia] = useState("");

  const canSubmit = nombre.trim().length >= 2 && edad >= 14 && edad <= 60;

  function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      nombre: nombre.trim(),
      edad: Number(edad),
      sustancia: sustancia || "",
      frecuencia: frecuencia || "",
    });
  }

  return (
    <motion.form
      onSubmit={handleSubmit}
      className="fixed inset-0 bg-black z-50 flex items-center justify-center p-6 overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="futuro-intake"
    >
      <div className="max-w-md w-full py-10">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-[10px] uppercase tracking-[0.4em] text-cyan-400 mb-6 font-mono"
        >
          &gt; VERIFICANDO IDENTIDAD_
        </motion.div>

        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="text-3xl sm:text-4xl font-light text-white mb-3 leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Tu yo del futuro<br />
          <GlitchText>quiere asegurarse de que eres tú.</GlitchText>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-neutral-500 text-xs mb-8"
        >
          Confirma tu identidad. Nada se guarda.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3 }}
          className="space-y-5"
        >
          {/* Nombre */}
          <div>
            <label className="block text-xs uppercase tracking-widest text-cyan-400 mb-2 font-mono">
              &gt; Dime tu nombre
            </label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="¿Cómo te llamas?"
              data-testid="futuro-input-nombre"
              className="w-full bg-neutral-950 border border-neutral-800 focus:border-cyan-500/60 focus:outline-none rounded-md px-4 py-3 text-white text-base placeholder:text-neutral-700 font-mono transition"
              maxLength={40}
              required
              autoFocus
            />
          </div>

          {/* Edad */}
          <div>
            <label className="block text-xs uppercase tracking-widest text-cyan-400 mb-2 font-mono">
              &gt; Confirma tu edad: <span className="text-white text-base font-bold">{edad}</span>
            </label>
            <input
              type="range"
              min={14}
              max={60}
              value={edad}
              onChange={(e) => setEdad(Number(e.target.value))}
              data-testid="futuro-input-edad"
              className="w-full accent-cyan-500"
            />
            <div className="flex justify-between text-[10px] text-neutral-600 mt-1 font-mono">
              <span>14</span><span>30</span><span>45</span><span>60</span>
            </div>
          </div>

          {/* Sustancia (opcional) */}
          <div>
            <label className="block text-xs uppercase tracking-widest text-cyan-400 mb-2 font-mono">
              &gt; ¿Me dirás qué consumes? <span className="text-neutral-600 normal-case tracking-normal">(opcional)</span>
            </label>
            <select
              value={sustancia}
              onChange={(e) => setSustancia(e.target.value)}
              data-testid="futuro-input-sustancia"
              className="w-full bg-neutral-950 border border-neutral-800 focus:border-cyan-500/60 focus:outline-none rounded-md px-4 py-3 text-white text-base font-mono"
            >
              <option value="">— prefiero no decir —</option>
              <option value="alcohol">Alcohol</option>
              <option value="marihuana">Marihuana</option>
              <option value="cocaína">Cocaína</option>
              <option value="tussi">Tussi</option>
              <option value="pasta base">Pasta base</option>
              <option value="varias sustancias">Varias</option>
            </select>
          </div>

          {/* Frecuencia (opcional) */}
          <div>
            <label className="block text-xs uppercase tracking-widest text-cyan-400 mb-2 font-mono">
              &gt; ¿Desde cuándo? <span className="text-neutral-600 normal-case tracking-normal">(opcional)</span>
            </label>
            <select
              value={frecuencia}
              onChange={(e) => setFrecuencia(e.target.value)}
              data-testid="futuro-input-frecuencia"
              className="w-full bg-neutral-950 border border-neutral-800 focus:border-cyan-500/60 focus:outline-none rounded-md px-4 py-3 text-white text-base font-mono"
            >
              <option value="">— prefiero no decir —</option>
              <option value="menos de 6 meses">Menos de 6 meses</option>
              <option value="1 año">Alrededor de 1 año</option>
              <option value="2-3 años">2 a 3 años</option>
              <option value="más de 3 años">Más de 3 años</option>
            </select>
          </div>
        </motion.div>

        <motion.button
          type="submit"
          disabled={!canSubmit}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6 }}
          data-testid="futuro-submit"
          className="mt-8 w-full group relative px-8 py-4 bg-red-500 hover:bg-red-400 disabled:bg-neutral-800 disabled:text-neutral-600 text-black disabled:cursor-not-allowed font-bold uppercase tracking-[0.2em] text-sm rounded-md transition-all shadow-[0_0_40px_rgba(239,68,68,0.4)] disabled:shadow-none font-mono"
        >
          Recibir el mensaje
          <ArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </motion.button>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.45 }}
          transition={{ delay: 2.2 }}
          className="text-center text-neutral-600 text-[10px] mt-4 font-mono"
        >
          &gt; CONEXIÓN_CIFRADA · NO_TELEMETRÍA
        </motion.p>
      </div>
    </motion.form>
  );
}

// ============================================================================
// 2) DATA REVEAL (Mr. Robot style terminal)
// ============================================================================
function DataReveal({ data, onDone }) {
  const [lineIdx, setLineIdx] = useState(0);

  const lines = useMemo(() => {
    const arr = [
      "> CONECTANDO CON 2030...",
      "> CANAL: SEÑAL TEMPORAL INESTABLE",
      "> AUTENTICANDO REMITENTE...",
      `> NOMBRE:         ${data.nombre}`,
      `> EDAD 2026:      ${data.edad} años`,
      `> EDAD 2030:      ${data.edad + 4} años`,
      `> AÑOS PERDIDOS:  4`,
    ];
    if (data.sustancia) arr.push(`> SUSTANCIA:      ${data.sustancia}`);
    if (data.frecuencia) arr.push(`> FRECUENCIA:     ${data.frecuencia}`);
    arr.push("> ESTADO 2030:   [CRÍTICO]");
    arr.push("> MENSAJE ENTRANTE...");
    return arr;
  }, [data]);

  useEffect(() => {
    if (lineIdx >= lines.length) {
      const t = setTimeout(onDone, 950);
      return () => clearTimeout(t);
    }
    const delay = lineIdx === lines.length - 1 ? 650 : 210 + Math.random() * 110;
    const t = setTimeout(() => setLineIdx((i) => i + 1), delay);
    return () => clearTimeout(t);
  }, [lineIdx, lines.length, onDone]);

  return (
    <motion.div
      className="fixed inset-0 bg-black z-50 flex items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="futuro-data-reveal"
    >
      <div className="max-w-lg w-full">
        <div className="bg-neutral-950/80 border border-cyan-900/40 rounded-md p-6 font-mono text-sm sm:text-base text-cyan-300/90 shadow-[0_0_60px_rgba(0,229,255,0.08)]">
          {lines.slice(0, lineIdx).map((l, i) => {
            const isCritical = l.includes("[CRÍTICO]");
            const isIncoming = l.includes("MENSAJE ENTRANTE");
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2 }}
                className={`leading-relaxed ${isCritical ? "text-red-400 font-bold" : ""} ${isIncoming ? "text-white animate-pulse" : ""}`}
              >
                {l}
              </motion.div>
            );
          })}
          <motion.span
            animate={{ opacity: [0.1, 1, 0.1] }}
            transition={{ duration: 0.9, repeat: Infinity }}
            className="inline-block w-2 h-4 bg-cyan-400 ml-1 align-middle"
          />
        </div>
      </div>
    </motion.div>
  );
}

// ============================================================================
// 3) RINGTONE — "mensaje entrante de número desconocido"
// ============================================================================
function FuturoRingtone({ nombre, onOpen }) {
  const initial = (nombre[0] || "N").toUpperCase();

  return (
    <motion.div
      className="fixed inset-0 bg-gradient-to-b from-neutral-900 via-black to-black z-40 flex flex-col items-center justify-between p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="futuro-ringtone"
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-center mt-14 font-mono"
      >
        <p className="text-neutral-500 text-[10px] uppercase tracking-[0.4em] mb-2">Mensaje entrante</p>
        <p className="text-cyan-400 text-sm">Señal inestable · 2030</p>
      </motion.div>

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: [1, 1.04, 1], opacity: 1 }}
        transition={{
          scale: { duration: 1.7, repeat: Infinity, ease: "easeInOut" },
          opacity: { duration: 0.5 },
        }}
        className="flex flex-col items-center"
      >
        <motion.div
          animate={{
            textShadow: [
              "2px 0 #ff3b3b, -2px 0 #00e5ff",
              "3px 0 #ff3b3b, -3px 0 #00e5ff",
              "1px 0 #ff3b3b, -1px 0 #00e5ff",
            ],
          }}
          transition={{ duration: 0.24, repeat: Infinity }}
          className="w-32 h-32 rounded-full bg-gradient-to-br from-red-500/40 via-neutral-900 to-cyan-500/40 border border-red-500/30 flex items-center justify-center text-6xl font-bold text-white mb-6 shadow-[0_0_100px_rgba(239,68,68,0.3)]"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          {initial}
        </motion.div>
        <p className="text-white text-2xl font-light mb-1 font-mono">Yo <span className="text-cyan-400">(2030)</span></p>
        <motion.p
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-neutral-500 text-sm font-mono"
        >
          +56 9 ▓▓▓▓ ▓▓▓▓
        </motion.p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="mb-14 flex flex-col items-center gap-3"
      >
        <button
          onClick={onOpen}
          data-testid="futuro-open-btn"
          className="group relative px-10 py-4 bg-red-500 hover:bg-red-400 text-black font-bold uppercase tracking-[0.2em] text-sm rounded-md transition-all hover:scale-105 shadow-[0_0_50px_rgba(239,68,68,0.5)] font-mono"
        >
          Abrir mensaje
          <ArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
        <p className="text-[10px] text-neutral-600 font-mono tracking-wider">&gt; No reconoces este número</p>
      </motion.div>
    </motion.div>
  );
}

// ============================================================================
// 4) CHAT SCENE (soporta checkboxes interactivos)
// ============================================================================
function FuturoChatScene({ sceneIndex, scene, nombre, onNext, onLiesMarked, onTestCompleted }) {
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [typing, setTyping] = useState(true);
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [lies, setLies] = useState([]); // indices seleccionados
  const [liesConfirmed, setLiesConfirmed] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [testQ, setTestQ] = useState(0); // índice pregunta actual
  const [testAnswers, setTestAnswers] = useState([]); // array de weights
  const [testConfirmed, setTestConfirmed] = useState(false);
  const chatRef = useRef(null);
  const endRef = useRef(null);
  const runIdRef = useRef(0);

  useEffect(() => {
    runIdRef.current += 1;
    const myRun = runIdRef.current;
    setVisibleMessages([]);
    setTyping(true);
    setShowCheckboxes(false);
    setLies([]);
    setLiesConfirmed(false);
    setShowTest(false);
    setTestQ(0);
    setTestAnswers([]);
    setTestConfirmed(false);

    const schedule = async () => {
      for (let i = 0; i < scene.messages.length; i++) {
        if (runIdRef.current !== myRun) return;
        const msg = scene.messages[i];
        setTyping(true);
        const typingMs = Math.max(650, Math.min(1700, (msg.text || "").length * 22 + (msg.image ? 600 : 0)));
        await sleep(typingMs);
        if (runIdRef.current !== myRun) return;
        setTyping(false);
        await sleep(140);
        if (runIdRef.current !== myRun) return;
        setVisibleMessages((prev) => (prev.length > i ? prev : [...prev, msg]));

        // Si este mensaje es el prompt del checkbox, mostrar y pausar aquí
        if (msg.isCheckboxPrompt && scene.lies) {
          await sleep(450);
          if (runIdRef.current !== myRun) return;
          setShowCheckboxes(true);
          return; // espera click del usuario (handleConfirmLies)
        }

        // Si este mensaje es el prompt del test de dependencia, mostrar el quiz
        if (msg.isTestPrompt && scene.isTest) {
          await sleep(500);
          if (runIdRef.current !== myRun) return;
          setShowTest(true);
          return; // espera respuestas del usuario
        }

        const readMs = Math.max(750, Math.min(1800, (msg.text || "").length * 24 + (msg.image ? 1100 : 0)));
        await sleep(readMs);
      }
      if (runIdRef.current !== myRun) return;
      setTyping(false);
      await sleep(1100);
      if (runIdRef.current === myRun) onNext();
    };
    schedule();
    return () => {
      runIdRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneIndex]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [visibleMessages, typing, showCheckboxes, liesConfirmed, showTest, testQ, testConfirmed]);

  function toggleLie(i) {
    setLies((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));
  }

  async function confirmLies() {
    setLiesConfirmed(true);
    setShowCheckboxes(false);
    if (onLiesMarked) onLiesMarked(lies.length);
    // Respuesta del "Yo futuro" al usuario
    const reply = [
      { text: `Te reconociste en ${lies.length} de ${scene.lies.length} mentiras.`, fromFuture: true },
      { text: lies.length >= 4
          ? `Yo también me las creí todas, ${nombre}.`
          : lies.length >= 1
            ? `Yo también me las creí, ${nombre}. Todas terminaron iguales.`
            : `Tal vez aún no te las dices. Pero vienen, ${nombre}. Yo las empecé igual.`,
        fromFuture: true },
    ];
    for (const m of reply) {
      setTyping(true);
      await sleep(900);
      setTyping(false);
      await sleep(140);
      setVisibleMessages((prev) => [...prev, m]);
      await sleep(1300);
    }
    await sleep(900);
    onNext();
  }

  // ---- Test de dependencia ----
  function selectTestAnswer(weight) {
    const nextAnswers = [...testAnswers, weight];
    setTestAnswers(nextAnswers);
    if (nextAnswers.length >= DEPENDENCE_QUESTIONS.length) {
      finishTest(nextAnswers);
    } else {
      setTestQ((q) => q + 1);
    }
  }

  async function finishTest(finalAnswers) {
    const score = finalAnswers.reduce((s, w) => s + w, 0);
    const level = scoreToLevel(score);
    setTestConfirmed(true);
    setShowTest(false);
    if (onTestCompleted) onTestCompleted({ score, level, answers: finalAnswers });

    // Breve pausa de "procesando"
    await sleep(1400);

    // Reacción del yo-futuro según el nivel detectado
    const reactions = buildReactionMessages(level, nombre);
    for (const m of reactions) {
      setTyping(true);
      await sleep(Math.max(700, Math.min(1500, m.text.length * 20)));
      setTyping(false);
      await sleep(140);
      setVisibleMessages((prev) => [...prev, m]);
      await sleep(Math.max(850, Math.min(1600, m.text.length * 26)));
    }
    await sleep(900);
    onNext();
  }

  const initial = (nombre[0] || "N").toUpperCase();

  return (
    <motion.div
      key={sceneIndex}
      className="fixed inset-0 z-30 flex flex-col bg-black"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {/* Header tipo WhatsApp distópico */}
      <div className="bg-gradient-to-b from-red-950/30 to-neutral-950 backdrop-blur-md border-b border-red-900/30 px-4 py-3 flex items-center gap-3 flex-shrink-0 relative z-10">
        <motion.div
          animate={{
            textShadow: [
              "1px 0 #ff3b3b, -1px 0 #00e5ff",
              "2px 0 #ff3b3b, -2px 0 #00e5ff",
              "1px 0 #ff3b3b, -1px 0 #00e5ff",
            ],
          }}
          transition={{ duration: 0.3, repeat: Infinity }}
          className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500/40 via-neutral-900 to-cyan-500/40 border border-red-500/40 flex items-center justify-center text-lg font-bold text-white font-mono"
        >
          {initial}
        </motion.div>
        <div className="flex-1">
          <p className="text-white text-base font-medium font-mono">
            Yo <span className="text-cyan-400">(2030)</span>
          </p>
          <p className="text-red-400 text-xs font-mono">{typing ? "escribiendo..." : "señal inestable"}</p>
        </div>
        <span className="text-neutral-500 text-[10px] uppercase tracking-wider hidden sm:block font-mono">
          {scene.label}
        </span>
      </div>

      {/* Chat body */}
      <div ref={chatRef} className="flex-1 overflow-y-auto px-4 py-6 space-y-2 relative z-10">
        <AnimatePresence initial={false} mode="popLayout">
          {visibleMessages.map((m, i) => (
            <motion.div
              layout
              key={`${sceneIndex}-${i}`}
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 320, damping: 26, mass: 0.7 }}
              className="flex"
            >
              <div
                className={`rounded-2xl rounded-bl-sm shadow-md max-w-[85%] sm:max-w-[70%] border ${
                  m.image ? "p-1.5" : "px-4 py-2.5 text-[15px]"
                } bg-neutral-900 text-white border-neutral-800`}
              >
                {m.image && (
                  <motion.div
                    initial={{ filter: "blur(6px) hue-rotate(90deg)", opacity: 0.3 }}
                    animate={{ filter: "blur(0px) hue-rotate(0deg)", opacity: 1 }}
                    transition={{ duration: 1.6, ease: "easeOut" }}
                    className="overflow-hidden rounded-xl mb-1 relative"
                  >
                    <img
                      src={m.image}
                      alt={m.imageCaption || "Mi 2030"}
                      loading="lazy"
                      decoding="async"
                      className="block w-full max-w-[280px] h-auto object-cover"
                    />
                  </motion.div>
                )}
                {m.text && (
                  <p className={m.image ? "text-[14px] text-neutral-200 px-2 pt-1 pb-1" : ""}>
                    {m.text}
                  </p>
                )}
                {m.imageCaption && (
                  <p className="text-[11px] text-neutral-500 px-2 pb-1.5 italic">{m.imageCaption}</p>
                )}
              </div>
            </motion.div>
          ))}

          {typing && (
            <motion.div
              layout
              key={`typing-${sceneIndex}`}
              initial={{ opacity: 0, y: 6, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92, transition: { duration: 0.18 } }}
              transition={{ type: "spring", stiffness: 340, damping: 28 }}
              className="flex"
            >
              <div className="bg-neutral-900 border border-neutral-800 rounded-2xl rounded-bl-sm px-4 py-3 shadow-md">
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="w-2 h-2 bg-neutral-500 rounded-full"
                      animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
                      transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.18 }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Checkboxes interactivos */}
          {showCheckboxes && scene.lies && (
            <motion.div
              layout
              key={`lies-${sceneIndex}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="w-full pt-2"
              data-testid="futuro-lies-box"
            >
              <div className="bg-neutral-950 border border-red-900/40 rounded-xl p-4 shadow-[0_0_40px_rgba(239,68,68,0.15)]">
                <p className="text-xs uppercase tracking-widest text-cyan-400 font-mono mb-3">
                  &gt; Marca las que te has dicho
                </p>
                <div className="space-y-2">
                  {scene.lies.map((txt, i) => {
                    const active = lies.includes(i);
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => toggleLie(i)}
                        data-testid={`futuro-lie-${i}`}
                        className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-md border transition-all ${
                          active
                            ? "bg-red-500/10 border-red-500/50 text-red-100"
                            : "bg-neutral-900/60 border-neutral-800 text-neutral-300 hover:border-neutral-700"
                        }`}
                      >
                        <div className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center mt-0.5 ${
                          active ? "bg-red-500 border-red-500" : "border-neutral-600"
                        }`}>
                          {active && <span className="text-black text-[10px] font-bold">✓</span>}
                        </div>
                        <span className="text-sm">"{txt}"</span>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={confirmLies}
                  data-testid="futuro-lies-confirm"
                  className="mt-4 w-full px-5 py-2.5 bg-red-500 hover:bg-red-400 text-black font-bold uppercase tracking-[0.15em] text-xs rounded-md transition font-mono"
                >
                  Continuar ({lies.length} marcadas)
                </button>
              </div>
            </motion.div>
          )}

          {/* Test de dependencia (5 preguntas secuenciales) */}
          {showTest && (
            <motion.div
              layout
              key={`test-${sceneIndex}-${testQ}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="w-full pt-2"
              data-testid="futuro-test-box"
            >
              <div className="bg-neutral-950 border border-cyan-900/40 rounded-xl p-4 shadow-[0_0_40px_rgba(0,229,255,0.1)]">
                {/* Progress */}
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-widest text-cyan-400 font-mono">
                    &gt; Pregunta {testQ + 1} de {DEPENDENCE_QUESTIONS.length}
                  </p>
                  <div className="flex gap-1">
                    {DEPENDENCE_QUESTIONS.map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 w-6 rounded-full transition-all ${
                          i < testQ ? "bg-cyan-400" : i === testQ ? "bg-cyan-500" : "bg-neutral-800"
                        }`}
                      />
                    ))}
                  </div>
                </div>

                <p className="text-white text-base sm:text-lg mb-4 leading-snug"
                   style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {DEPENDENCE_QUESTIONS[testQ].prompt}
                </p>

                <div className="space-y-2">
                  {DEPENDENCE_QUESTIONS[testQ].options.map((opt, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => selectTestAnswer(opt.weight)}
                      data-testid={`futuro-test-q${testQ}-opt${i}`}
                      className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-md border border-neutral-800 bg-neutral-900/60 text-neutral-200 hover:border-cyan-500/60 hover:bg-cyan-500/5 transition-all"
                    >
                      <div className="flex-shrink-0 w-4 h-4 rounded-full border border-neutral-600" />
                      <span className="text-sm">{opt.label}</span>
                    </button>
                  ))}
                </div>

                <p className="text-[10px] text-neutral-600 font-mono mt-3 text-center">
                  &gt; responde con honestidad · nadie más verá esto
                </p>
              </div>
            </motion.div>
          )}

          {/* "Procesando recuerdo..." después del test */}
          {testConfirmed && !showTest && visibleMessages.length === scene.messages.length && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-2 text-cyan-400 text-xs font-mono py-2"
            >
              <motion.span
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1.2, repeat: Infinity }}
              >
                &gt; procesando recuerdo...
              </motion.span>
            </motion.div>
          )}
        </AnimatePresence>
        <div ref={endRef} style={{ height: 1 }} />
      </div>
    </motion.div>
  );
}

// ============================================================================
// 5) IMPACT SCREEN — nombre del usuario en pantalla
// ============================================================================
function ImpactScreen({ nombre, edadFuturo, onNext }) {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 1100);
    const t2 = setTimeout(() => setStage(2), 2900);
    const t3 = setTimeout(() => setStage(3), 5000);
    const t4 = setTimeout(() => setStage(4), 7200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);

  return (
    <motion.div
      className="fixed inset-0 z-40 bg-black flex flex-col items-center justify-center p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      data-testid="futuro-impact"
    >
      {stage >= 1 && (
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 2, ease: "easeOut" }}
          className="text-neutral-400 text-xl sm:text-2xl font-light text-center mb-4"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Este mensaje es real.
        </motion.p>
      )}
      {stage >= 2 && (
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 2 }}
          className="text-white text-5xl sm:text-7xl font-light text-center mt-8"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {nombre}.
        </motion.p>
      )}
      {stage >= 3 && (
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 2 }}
          className="text-neutral-300 text-lg sm:text-2xl font-light text-center mt-10 max-w-md"
        >
          No esperes a los {edadFuturo}.
        </motion.p>
      )}
      {stage >= 4 && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4 }}
          onClick={onNext}
          data-testid="futuro-impact-continue"
          className="mt-16 text-neutral-500 hover:text-neutral-200 text-sm underline underline-offset-8 transition-colors font-mono"
        >
          Continuar →
        </motion.button>
      )}
    </motion.div>
  );
}

// ============================================================================
// 6) ALTERNATE FUTURE — Yo 2030 recuperado
// ============================================================================
function AlternateFutureScene({ nombre, bracket, level, onDone }) {
  const messages = useMemo(() => buildAlternateScript(bracket, { nombre }, level), [bracket, nombre, level]);
  const [visible, setVisible] = useState([]);
  const [typing, setTyping] = useState(true);
  const endRef = useRef(null);
  const runIdRef = useRef(0);

  useEffect(() => {
    runIdRef.current += 1;
    const myRun = runIdRef.current;
    const schedule = async () => {
      for (let i = 0; i < messages.length; i++) {
        if (runIdRef.current !== myRun) return;
        setTyping(true);
        const msg = messages[i];
        await sleep(Math.max(650, Math.min(1500, msg.text.length * 20)));
        if (runIdRef.current !== myRun) return;
        setTyping(false);
        await sleep(130);
        setVisible((prev) => (prev.length > i ? prev : [...prev, msg]));
        await sleep(Math.max(700, Math.min(1600, msg.text.length * 22)));
      }
      setTyping(false);
      await sleep(900);
      if (runIdRef.current === myRun) onDone();
    };
    schedule();
    return () => { runIdRef.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [visible, typing]);

  const initial = (nombre[0] || "N").toUpperCase();

  return (
    <motion.div
      className="fixed inset-0 z-40 flex flex-col bg-gradient-to-b from-emerald-950/30 via-neutral-950 to-black"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
      data-testid="futuro-alternate"
    >
      <div className="bg-emerald-900/30 backdrop-blur-md border-b border-emerald-900/40 px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-700 flex items-center justify-center text-lg font-bold text-black font-mono">
          {initial}
        </div>
        <div className="flex-1">
          <p className="text-white text-base font-medium font-mono">
            Yo <span className="text-emerald-400">(2030 · bueno)</span>
          </p>
          <p className="text-emerald-400 text-xs font-mono">{typing ? "escribiendo..." : "señal estable"}</p>
        </div>
        <span className="text-emerald-400 text-[10px] uppercase tracking-wider hidden sm:block font-mono">
          Otro final
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-2">
        <AnimatePresence initial={false} mode="popLayout">
          {visible.map((m, i) => (
            <motion.div
              layout
              key={i}
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 320, damping: 26 }}
              className="flex"
            >
              <div className={m.anchor
                ? "rounded-2xl rounded-bl-sm bg-emerald-600/25 border border-emerald-400/60 text-white text-base sm:text-lg font-medium px-5 py-3 max-w-[90%] sm:max-w-[75%] shadow-[0_0_30px_rgba(16,185,129,0.35)]"
                : "rounded-2xl rounded-bl-sm bg-emerald-900/30 border border-emerald-700/40 text-white text-[15px] px-4 py-2.5 max-w-[85%] sm:max-w-[70%] shadow-md"
              }
                   style={m.anchor ? { fontFamily: "'Playfair Display', Georgia, serif" } : {}}>
                {m.text}
              </div>
            </motion.div>
          ))}
          {typing && (
            <motion.div
              layout
              key="typing-alt"
              initial={{ opacity: 0, y: 6, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ type: "spring", stiffness: 340, damping: 28 }}
              className="flex"
            >
              <div className="bg-emerald-900/30 border border-emerald-700/40 rounded-2xl rounded-bl-sm px-4 py-3 shadow-md">
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="w-2 h-2 bg-emerald-400/80 rounded-full"
                      animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
                      transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.18 }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div ref={endRef} style={{ height: 1 }} />
      </div>
    </motion.div>
  );
}

// ============================================================================
// 7) FINAL CTA
// ============================================================================
function FuturoCTA({ nombre, liesCount, level, onAskHelp, onShare }) {
  const rec = level ? recommendationForLevel(level) : null;
  const urgencyColor = {
    normal: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
    alta: "text-amber-400 border-amber-500/40 bg-amber-500/10",
    urgente: "text-red-400 border-red-500/40 bg-red-500/10",
    critica: "text-red-300 border-red-400/60 bg-red-500/15",
  };
  const urgencyClass = rec ? (urgencyColor[rec.urgency] || urgencyColor.urgente) : urgencyColor.urgente;
  return (
    <motion.div
      className="fixed inset-0 z-40 bg-gradient-to-b from-neutral-950 via-black to-black overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2 }}
      data-testid="futuro-cta"
    >
      <Header />
      <div className="min-h-screen flex items-center justify-center p-6 pt-24">
      <div className="max-w-2xl text-center py-10">
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-cyan-400 text-[10px] uppercase tracking-[0.4em] mb-6 font-mono"
        >
          &gt; DESCONECTANDO 2030_
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 1 }}
          className="text-4xl sm:text-6xl font-light text-white mb-6 leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {nombre}, todavía<br className="sm:hidden" /> te queda tiempo.
        </motion.h2>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="space-y-2 text-neutral-300 text-lg sm:text-xl mb-10"
        >
          <p>No esperes a que tu familia se enferme.</p>
          <p>No esperes a perderlo todo.</p>
          <p className="pt-3 text-white text-xl sm:text-2xl font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Pide ayuda hoy.
          </p>
          <p className="text-red-400 font-medium">Es el acto más valiente que puedes hacer.</p>
        </motion.div>

        {typeof liesCount === "number" && liesCount > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 2.4 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-full text-red-300 text-xs font-mono mb-8"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            Te reconociste en {liesCount} mentiras.
          </motion.div>
        )}

        {/* ========================== TARJETA DE RECOMENDACIÓN CLÍNICA ========================== */}
        {rec && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.2, duration: 0.9 }}
            className={`text-left mx-auto max-w-md mb-10 border-2 rounded-xl p-6 backdrop-blur ${urgencyClass}`}
            data-testid="futuro-recommendation-card"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] mb-4 font-mono">
              <span>⚕</span>
              <span>Tu nivel detectado</span>
            </div>
            <p className="text-xl sm:text-2xl font-light mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {rec.levelLabel}
            </p>

            <div className="my-5 h-px bg-current opacity-20" />

            <p className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2 font-mono">
              &gt; Tratamiento aconsejado
            </p>
            <p className="text-white text-lg sm:text-xl font-semibold">
              {rec.modalityLabel}
            </p>
            <p className="text-neutral-400 text-sm mt-1">
              {rec.modalitySubtitle}
            </p>

            <div className="mt-4 pt-4 border-t border-current border-opacity-20">
              <p className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2 font-mono">
                &gt; Por qué esto
              </p>
              <p className="text-neutral-200 text-sm leading-relaxed italic">
                "{rec.reason}"
              </p>
            </div>
          </motion.div>
        )}
        {/* ======================================================================================= */}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.8, duration: 0.8 }}
          className="space-y-4"
        >
          <button
            onClick={onAskHelp}
            data-testid="futuro-help-btn"
            className="w-full sm:w-auto px-10 sm:px-12 py-5 bg-red-500 hover:bg-red-400 text-black text-base sm:text-lg font-bold uppercase tracking-[0.12em] rounded-md transition-all hover:scale-105 shadow-[0_0_60px_rgba(239,68,68,0.5)] font-mono"
          >
            <Zap className="inline-block w-5 h-5 mr-2" />
            {rec ? `Cotizar ${rec.modalityLabel.toLowerCase()} · Gratis` : "Cambiar el final · Cotizar gratis"}
          </button>

          {rec && (
            <div className="pt-1">
              <button
                onClick={onAskHelp}
                data-testid="futuro-other-modalities-btn"
                className="text-neutral-500 hover:text-neutral-300 text-xs underline underline-offset-4 transition"
              >
                ¿Prefieres ver otras modalidades? · Cotizar sin recomendación
              </button>
            </div>
          )}

          <div className="pt-4 max-w-md mx-auto bg-red-950/20 border border-red-900/40 rounded-lg p-4 text-left" data-testid="futuro-helplines">
            <p className="text-red-200 text-[11px] uppercase tracking-widest font-mono mb-2 text-center">
              Si necesitas ayuda <span className="text-red-300">ahora</span>
            </p>
            <div className="space-y-1.5 text-xs sm:text-sm text-neutral-200 leading-relaxed">
              <p>
                <a href="tel:1412" data-testid="futuro-senda-btn" className="text-emerald-300 hover:text-emerald-200 font-semibold underline underline-offset-2">SENDA · 1412</a>
                <span className="text-neutral-400"> — orientación en adicciones, gratis 24/7</span>
              </p>
              <p>
                <a href="tel:6003607777" className="text-emerald-300 hover:text-emerald-200 font-semibold underline underline-offset-2">Salud Responde · 600 360 7777</a>
                <span className="text-neutral-400"> — apoyo emocional y crisis suicida</span>
              </p>
              <p>
                <a href="tel:131" className="text-emerald-300 hover:text-emerald-200 font-semibold underline underline-offset-2">SAMU · 131</a>
                <span className="text-neutral-400"> — emergencia médica</span>
              </p>
            </div>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 4 }}
            className="pt-6 text-white text-lg sm:text-xl font-light"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            No estás solo.
          </motion.p>

          <div className="pt-2">
            <button
              onClick={onShare}
              data-testid="futuro-share-btn"
              className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-300 text-sm transition-colors font-mono"
            >
              <Share2 className="w-4 h-4" /> Reenviar a alguien que no se da cuenta
            </button>
          </div>
        </motion.div>
      </div>
      </div>
    </motion.div>
  );
}

// ============================================================================
// MAIN
// ============================================================================
export default function ExperienciaYoFuturo() {
  const navigate = useNavigate();
  const [step, setStep] = useState(-1);
  // -1=intro cinematic, 0=form, 1=datareveal, 2=ringtone, 3=chat, 4=impact, 5=alternate, 6=cta
  const [data, setData] = useState(null); // { nombre, edad, edadFuturo, sustancia, frecuencia }
  const [chatIdx, setChatIdx] = useState(0);
  const [liesCount, setLiesCount] = useState(0);
  const [testResult, setTestResult] = useState(null); // { score, level, answers }

  const canonical = `${SITE}/yo-del-futuro`;

  const script = useMemo(() => {
    if (!data) return null;
    return buildScript(data.bracket, data, testResult?.level);
  }, [data, testResult]);

  function handleIntake(payload) {
    const edadFuturo = payload.edad + 5;
    const bracket = ageBracket(payload.edad);
    setData({ ...payload, edadFuturo, bracket });
    setStep(1);
    import('../lib/track').then(m => m.trackEvent('yo_futuro_form_submitted', '/yo-del-futuro'));
  }

  function handleChatNext() {
    if (!script) return;
    if (chatIdx < script.scenes.length - 1) {
      setChatIdx(chatIdx + 1);
    } else {
      setStep(4);
      import('../lib/track').then(m => m.trackEvent('yo_futuro_completed', '/yo-del-futuro'));
    }
  }

  function handleAskHelp() {
    const bracket = data?.bracket || "";
    const level = testResult?.level || "";
    const rec = level ? recommendationForLevel(level) : null;
    const params = new URLSearchParams();
    params.set("source", level ? `yo-futuro-${bracket}-${level}` : `yo-futuro-${bracket}`);
    if (rec) params.set("modalidad_sugerida", rec.modality);
    if (data?.nombre) params.set("nombre", data.nombre);
    navigate(`/cotizar?${params.toString()}`);
  }

  async function handleShare() {
    const shareData = {
      title: "Un mensaje desde 2030",
      text: "Un amigo te envió un mensaje. Es tu yo del futuro. Ábrelo.",
      url: canonical,
    };
    try {
      if (navigator.share) await navigator.share(shareData);
      else {
        await navigator.clipboard.writeText(canonical);
        alert("Link copiado al portapapeles");
      }
    } catch {}
  }

  function handleSkipToEnd() {
    setStep(6);
  }

  const progressPct = useMemo(() => {
    if (step === 0) return 0;
    if (step === 1) return 5;
    if (step === 2) return 10;
    if (step === 3) return 20 + chatIdx * 15;
    if (step === 4) return 80;
    if (step === 5) return 90;
    return 100;
  }, [step, chatIdx]);

  const currentScene = script?.scenes[chatIdx];

  return (
    <>
      
        <title>Un mensaje desde 2030 — Historia de Ti mismo | SinAdicciones.org</title>
        <meta
          name="description"
          content="¿Y si tu yo del futuro pudiera advertirte? Una experiencia inmersiva personalizada sobre adicción en Chile. Cambia tu 2030 hoy."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content="¿Y si tu yo del futuro pudiera advertirte? | SinAdicciones.org" />
        <meta property="og:description" content="Un mensaje personalizado desde 2030. Una experiencia que podría salvar tu vida." />
        <meta property="og:image" content={`${SITE}/og-experiencia-yo-futuro.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="¿Y si tu yo del futuro pudiera advertirte?" />
        <meta name="twitter:description" content="Un mensaje desde 2030 que podría salvar tu vida." />
        <meta name="twitter:image" content={`${SITE}/og-experiencia-yo-futuro.jpg`} />
        <link
          href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500;600&display=swap"
          rel="stylesheet"
        />
      

      <div className="fixed inset-0 bg-black" data-testid="futuro-root">
        <NoiseBg />

        {/* Progress bar */}
        {step > 0 && step < 6 && (
          <div className="fixed top-0 left-0 right-0 h-0.5 bg-neutral-900 z-[60]">
            <motion.div
              className="h-full bg-gradient-to-r from-red-500 via-red-400 to-cyan-400"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.6 }}
            />
          </div>
        )}

        {/* Close */}
        {step >= 0 && step < 6 && (
          <button
            onClick={handleSkipToEnd}
            data-testid="futuro-close-btn"
            aria-label="Cerrar"
            className="fixed top-3 right-3 z-[55] w-9 h-9 rounded-full bg-neutral-900/80 backdrop-blur text-neutral-300 hover:text-white flex items-center justify-center border border-neutral-800"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        <AnimatePresence mode="wait">
          {step === -1 && <IntroCinematic key="intro" onContinue={() => { setStep(0); import('../lib/track').then(m => m.trackEvent('yo_futuro_started', '/yo-del-futuro')); }} />}
          {step === 0 && <IntakeForm key="intake" onSubmit={handleIntake} />}
          {step === 1 && data && (
            <DataReveal key="datareveal" data={data} onDone={() => setStep(2)} />
          )}
          {step === 2 && data && (
            <FuturoRingtone key="ringtone" nombre={data.nombre} onOpen={() => setStep(3)} />
          )}
          {step === 3 && script && currentScene && (
            <FuturoChatScene
              key={`chat-${chatIdx}`}
              sceneIndex={chatIdx}
              scene={currentScene}
              nombre={data.nombre}
              onNext={handleChatNext}
              onLiesMarked={(n) => setLiesCount(n)}
              onTestCompleted={(r) => setTestResult(r)}
            />
          )}
          {step === 4 && data && (
            <ImpactScreen
              key="impact"
              nombre={data.nombre}
              edadFuturo={data.edadFuturo}
              onNext={() => setStep(5)}
            />
          )}
          {step === 5 && data && (
            <AlternateFutureScene
              key="alternate"
              nombre={data.nombre}
              bracket={data.bracket}
              level={testResult?.level}
              onDone={() => setStep(6)}
            />
          )}
          {step === 6 && data && (
            <FuturoCTA
              key="cta"
              nombre={data.nombre}
              liesCount={liesCount}
              level={testResult?.level}
              onAskHelp={handleAskHelp}
              onShare={handleShare}
            />
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
